import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Pause, Play } from '@digital-twins/design-system/icons'

import { cn } from '@/lib/utils'
import { buildReelPages, DEFAULT_REEL_PLAYER_LABELS, ReelPlayerLabels } from './reelPages'

import { ResolvedStudyReel } from '@digital-twins/shared-types/reporting'

import '../../foundations/tokens.css'
import './ReelPlayer.css'

/**
 * The study reel: a 9:16 player for `ResolvedStudyReel` (`study-reel.v1`).
 *
 * Ported from the Gate 1 review gallery the owner approved
 * (`apps/python-api/scripts/study_reel/reel.js`, `reel.css`): a page turn between
 * slides (a crossfade under `prefers-reduced-motion`), a draggable progress bar,
 * tap (left half back, right half forward), swipe and the arrow keys, and autoplay.
 *
 * Colors come from the app's semantic tokens (`--primary`, `--primary-dark`,
 * `--background`, `--foreground`, `--border`) and the deck's chart tokens, so the
 * player follows light and dark and a white-label brand. No label is uppercase or
 * letter-spaced.
 *
 * Text that does not fit its page shrinks in steps down to 0.7x; a page that still
 * overflows scrolls, is marked `data-overflow="true"` and is never cut.
 */

export { DEFAULT_REEL_PLAYER_LABELS }

export interface ReelPlayerProps {
  reel: ResolvedStudyReel
  labels?: ReelPlayerLabels
  /** When set, the next-step page shows a button that calls it. */
  onOpenReport?: () => void
  /** Focus the stage on mount, so the arrow keys work at once. */
  autoFocus?: boolean
  initialPage?: number
  /**
   * `full` (default): previous, next, play and the page name and count under the
   * stage. `play`: one play/pause button on the stage; the progress lines are
   * the only way to change pages (tap or drag), plus swipe and the arrow keys.
   */
  controls?: 'full' | 'play' | 'none'
  /**
   * `halves` (default): a tap on the left half goes back, the right half forward.
   * `theater`: the middle third plays or pauses (an icon flashes), the outer
   * thirds turn pages; for a reel watched full screen with no buttons.
   */
  tapZones?: 'halves' | 'theater'
  /** A vertical swipe on the stage: 1 is up (the next item), -1 is down. */
  onSwipeVertical?: (direction: 1 | -1) => void
  /** When set, a tap on the stage calls it instead of turning the page (swipes still turn). */
  onStageTap?: () => void
  /** Start playing on mount. */
  autoPlay?: boolean
  /** No drop shadow around the stage, for a host that sits it on the page. */
  isFlat?: boolean
  /**
   * Plays while a mouse or pen hovers the reel and pauses in place when it
   * leaves, like a muted preview. Touch never triggers it, and neither does a
   * viewer who prefers reduced motion.
   */
  playOnHover?: boolean
  /**
   * A host control in the stage's bottom-right corner (for example, open full
   * screen), shown on hover or keyboard focus. A tap on it never reaches the stage.
   */
  stageAction?: React.ReactNode
  /**
   * `full` (default): every line the reel carries. `short`: reads like
   * short-form video, with bigger type, captions clamped to two lines, step
   * titles without their detail, and no source line.
   */
  density?: 'full' | 'short'
  className?: string
  'data-testid'?: string
}

const FLIP_MS = 700

const FADE_MS = 320

const AUTOPLAY_MS = 5000

const FIT_STEPS = [1, 0.94, 0.88, 0.82, 0.76, 0.7]

const SWIPE_MIN_PX = 40

const TAP_MAX_PX = 12

const PAGE_STATE_CLASSES = ['is-on', 'is-anim', 'is-turned', 'is-dim', 'is-hide']

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** How far a page's content runs past its box, in px (layout boxes, not scrollHeight). */
function overflowPx(page: HTMLElement): number {
  const box = page.querySelector<HTMLElement>('.reel-sc')
  if (!box) {
    return 0
  }
  const rect = box.getBoundingClientRect()
  const style = window.getComputedStyle(box)
  let bottom = -Infinity
  let right = -Infinity
  Array.from(box.children).forEach((child) => {
    const childRect = child.getBoundingClientRect()
    bottom = Math.max(bottom, childRect.bottom)
    right = Math.max(right, childRect.right)
  })
  const vertical = bottom - (rect.bottom - (parseFloat(style.paddingBottom) || 0))
  const horizontal = right - (rect.right - (parseFloat(style.paddingRight) || 0))
  const over = Math.max(vertical, horizontal)
  return Number.isFinite(over) ? over : 0
}

function fitPage(page: HTMLElement) {
  let step = 0
  page.style.setProperty('--reel-fit', String(FIT_STEPS[0]))
  while (overflowPx(page) > 1 && step < FIT_STEPS.length - 1) {
    step += 1
    page.style.setProperty('--reel-fit', String(FIT_STEPS[step]))
  }
  const isOverflowing = overflowPx(page) > 1
  page.dataset.overflow = isOverflowing ? 'true' : 'false'
  if (isOverflowing) {
    console.warn('ReelPlayer/fit - page overflows its box', { kind: page.dataset.kind, fit: FIT_STEPS[step] })
  }
}

/** A progress line's fill: done pages full, later pages empty, the current one where it stands. */
function currentFillWidth(index: number, shown: number, isPlaying: boolean, partial: number): string {
  if (index < shown) {
    return '100%'
  }
  if (index > shown) {
    return '0%'
  }
  if (isPlaying || partial > 0) {
    return `${partial * 100}%`
  }
  return '100%'
}

export function ReelPlayer(props: ReelPlayerProps) {
  const {
    reel,
    labels = DEFAULT_REEL_PLAYER_LABELS,
    onOpenReport,
    autoFocus = false,
    initialPage = 0,
    controls = 'full',
    onStageTap,
    tapZones = 'halves',
    onSwipeVertical,
    autoPlay = false,
    isFlat = false,
    playOnHover = false,
    stageAction,
    density = 'full',
    className,
    'data-testid': dataTestId = 'reel-player',
  } = props

  // A host's inline onOpenReport is a new function every render; the pages are
  // rebuilt only when the reel, the labels or the button's presence change, and
  // the button always calls the latest handler.
  const onOpenReportRef = useRef(onOpenReport)
  const hasOpenReport = onOpenReport !== undefined
  const openReport = useCallback(() => onOpenReportRef.current?.(), [])
  const pages = useMemo(
    () => buildReelPages(reel, labels, hasOpenReport ? openReport : undefined),
    [reel, labels, hasOpenReport, openReport],
  )
  const total = pages.length
  const lastPage = Math.max(0, total - 1)
  const firstPage = Math.min(lastPage, Math.max(0, initialPage))

  const [current, setCurrent] = useState(firstPage)
  const [isPlaying, setIsPlaying] = useState(false)
  // How far the current page had played when it was paused (0 to 1): the fill
  // holds there and play resumes from it. 0 when nothing is partway.
  const [partial, setPartial] = useState(0)
  // Bumped on each theater play/pause tap: re-keys the flashed icon so it replays.
  const [flash, setFlash] = useState({ key: 0, isPlaying: false })
  // A new reel with fewer pages than the current index shows its last page.
  const shown = Math.min(current, lastPage)

  const stageRef = useRef<HTMLDivElement | null>(null)
  const barRef = useRef<HTMLDivElement | null>(null)
  const pageRefs = useRef<Array<HTMLElement | null>>([])
  const fillRefs = useRef<Array<HTMLElement | null>>([])
  const currentRef = useRef(firstPage)
  const playingRef = useRef(false)
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const frameRef = useRef(0)
  const startedAt = useRef(0)
  const partialRef = useRef(0)
  const pointer = useRef({ isDown: false, x: 0, y: 0 })
  const isScrubbing = useRef(false)

  const rest = useCallback((index: number) => {
    pageRefs.current.forEach((page, k) => {
      if (!page) {
        return
      }
      page.classList.remove(...PAGE_STATE_CLASSES)
      page.style.zIndex = ''
      page.setAttribute('aria-hidden', k === index ? 'false' : 'true')
      if (k === index) {
        page.classList.add('is-on')
        page.style.zIndex = '2'
      }
    })
  }, [])

  const settle = useCallback(() => {
    if (settleTimer.current) {
      clearTimeout(settleTimer.current)
    }
    settleTimer.current = null
    rest(currentRef.current)
  }, [rest])

  const go = useCallback(
    (requested: number, isInstant = false) => {
      if (!Number.isFinite(requested)) {
        return
      }
      const target = Math.max(0, Math.min(total - 1, requested))
      if (settleTimer.current) {
        settle()
      }
      const from = currentRef.current
      if (target === from) {
        return
      }
      currentRef.current = target
      startedAt.current = typeof performance === 'undefined' ? Date.now() : performance.now()
      partialRef.current = 0
      setPartial(0)
      setCurrent(target)
      const leaving = pageRefs.current[from]
      const entering = pageRefs.current[target]
      if (isInstant || !leaving || !entering) {
        rest(target)
        return
      }
      if (prefersReducedMotion()) {
        leaving.style.zIndex = '2'
        entering.classList.add('is-on', 'is-hide')
        entering.style.zIndex = '3'
        void entering.offsetWidth
        entering.classList.add('is-anim')
        entering.classList.remove('is-hide')
        settleTimer.current = setTimeout(settle, FADE_MS + 30)
        return
      }
      if (target > from) {
        entering.classList.add('is-on', 'is-dim')
        entering.style.zIndex = '1'
        leaving.style.zIndex = '3'
        void leaving.offsetWidth
        leaving.classList.add('is-anim', 'is-turned')
        entering.classList.add('is-anim')
        entering.classList.remove('is-dim')
      } else {
        entering.classList.add('is-on', 'is-turned')
        entering.style.zIndex = '3'
        leaving.style.zIndex = '1'
        void entering.offsetWidth
        entering.classList.add('is-anim')
        entering.classList.remove('is-turned')
        leaving.classList.add('is-anim', 'is-dim')
      }
      settleTimer.current = setTimeout(settle, FLIP_MS + 40)
    },
    [rest, settle, total],
  )

  /** Pauses in place; `isAtEnd` when the reel ran out, so nothing is left partway. */
  const stop = useCallback((isAtEnd = false) => {
    if (playingRef.current && !isAtEnd) {
      const progress = Math.min((performance.now() - startedAt.current) / AUTOPLAY_MS, 1)
      partialRef.current = progress < 1 ? Math.max(progress, 0) : 0
      setPartial(partialRef.current)
    }
    if (isAtEnd) {
      partialRef.current = 0
      setPartial(0)
    }
    playingRef.current = false
    cancelAnimationFrame(frameRef.current)
    setIsPlaying(false)
  }, [])

  const next = useCallback(() => {
    if (currentRef.current === total - 1) {
      stop(true)
    }
    go(currentRef.current + 1)
  }, [go, stop, total])

  const previous = useCallback(() => {
    go(currentRef.current - 1)
  }, [go])

  const tick = useCallback(
    (now: number) => {
      if (!playingRef.current) {
        return
      }
      const progress = Math.min((now - startedAt.current) / AUTOPLAY_MS, 1)
      const fill = fillRefs.current[currentRef.current]
      if (fill) {
        fill.style.width = `${progress * 100}%`
      }
      if (progress >= 1) {
        if (currentRef.current < total - 1) {
          go(currentRef.current + 1)
        } else {
          stop(true)
          return
        }
      }
      frameRef.current = requestAnimationFrame(tick)
    },
    [go, stop, total],
  )

  const play = useCallback(() => {
    if (playingRef.current) {
      return
    }
    if (currentRef.current === total - 1 && partialRef.current === 0) {
      go(0, true)
    }
    playingRef.current = true
    startedAt.current = performance.now() - partialRef.current * AUTOPLAY_MS
    setIsPlaying(true)
    frameRef.current = requestAnimationFrame(tick)
  }, [go, tick, total])

  useLayoutEffect(() => {
    onOpenReportRef.current = onOpenReport
  }, [onOpenReport])

  useLayoutEffect(() => {
    if (currentRef.current > lastPage) {
      currentRef.current = lastPage
      setCurrent(lastPage)
    }
    rest(currentRef.current)
  }, [rest, pages, lastPage])

  useLayoutEffect(() => {
    const fitAll = () => {
      pageRefs.current.forEach((page) => {
        if (page) {
          fitPage(page)
        }
      })
    }
    fitAll()
    let isCancelled = false
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts
    fonts?.ready?.then(() => {
      if (!isCancelled) {
        fitAll()
      }
    })
    const stage = stageRef.current
    const observer = typeof ResizeObserver === 'undefined' || !stage ? null : new ResizeObserver(() => fitAll())
    if (observer && stage) {
      observer.observe(stage)
    }
    return () => {
      isCancelled = true
      observer?.disconnect()
    }
  }, [pages])

  useEffect(() => {
    if (autoFocus) {
      stageRef.current?.focus()
    }
  }, [autoFocus])

  // Mount only: a host that opens the reel to watch it starts it playing.
  useEffect(() => {
    if (autoPlay) {
      play()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      cancelAnimationFrame(frameRef.current)
      if (settleTimer.current) {
        clearTimeout(settleTimer.current)
      }
    }
  }, [])

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (target.closest('input, textarea, select, [contenteditable="true"]')) {
      return
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      next()
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      previous()
    }
    if (event.key === ' ' && tapZones === 'theater') {
      event.preventDefault()
      togglePlay()
    }
  }

  const togglePlay = () => {
    const isNowPlaying = !playingRef.current
    if (isNowPlaying) {
      play()
    } else {
      stop()
    }
    setFlash((current) => ({ key: current.key + 1, isPlaying: isNowPlaying }))
  }

  const onPlayerPointerEnter = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!playOnHover || event.pointerType === 'touch' || prefersReducedMotion()) {
      return
    }
    play()
  }

  const onPlayerPointerLeave = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!playOnHover || event.pointerType === 'touch') {
      return
    }
    stop()
  }

  const onStagePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('.reel-rbar, button')) {
      return
    }
    pointer.current = { isDown: true, x: event.clientX, y: event.clientY }
  }

  const onStagePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointer.current.isDown) {
      return
    }
    pointer.current.isDown = false
    const dx = event.clientX - pointer.current.x
    const dy = event.clientY - pointer.current.y
    if (onSwipeVertical && Math.abs(dy) > SWIPE_MIN_PX && Math.abs(dy) > Math.abs(dx)) {
      onSwipeVertical(dy < 0 ? 1 : -1)
      return
    }
    if (Math.abs(dx) > SWIPE_MIN_PX && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) {
        next()
      } else {
        previous()
      }
      return
    }
    if (Math.abs(dx) < TAP_MAX_PX && Math.abs(dy) < TAP_MAX_PX) {
      if (onStageTap) {
        onStageTap()
        return
      }
      const rect = event.currentTarget.getBoundingClientRect()
      const offset = (event.clientX - rect.left) / rect.width
      if (tapZones === 'theater' && offset > 1 / 3 && offset < 2 / 3) {
        togglePlay()
        return
      }
      if (offset < 0.5) {
        previous()
      } else {
        next()
      }
    }
  }

  const scrubTo = (clientX: number) => {
    const bar = barRef.current
    if (!bar) {
      return
    }
    const rect = bar.getBoundingClientRect()
    const ratio = rect.width > 0 ? (clientX - rect.left) / rect.width : 0
    go(Math.floor(Math.min(Math.max(ratio, 0), 0.9999) * total), true)
  }

  const onBarPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    isScrubbing.current = true
    event.currentTarget.setPointerCapture?.(event.pointerId)
    scrubTo(event.clientX)
    event.preventDefault()
  }

  const onBarPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isScrubbing.current) {
      scrubTo(event.clientX)
    }
  }

  const onBarPointerUp = () => {
    isScrubbing.current = false
  }

  const onBarKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Home') {
      event.preventDefault()
      go(0, true)
    }
    if (event.key === 'End') {
      event.preventDefault()
      go(total - 1, true)
    }
  }

  const currentPage = pages[shown]
  const isLightPage = currentPage?.surface === 'paper'

  if (total === 0) {
    return null
  }

  const playButton = (
    <button
      type="button"
      aria-label={isPlaying ? labels.pause : labels.autoplay}
      aria-pressed={isPlaying}
      onClick={() => (isPlaying ? stop() : play())}
      data-testid="reel-autoplay"
    >
      {isPlaying ? <Pause aria-hidden /> : <Play aria-hidden />}
    </button>
  )

  return (
    <div
      className={cn(
        'reel-player',
        isFlat && 'is-flat',
        controls === 'play' && 'has-play-float',
        density === 'short' && 'is-short',
        className,
      )}
      data-testid={dataTestId}
      onKeyDown={onKeyDown}
      onPointerEnter={onPlayerPointerEnter}
      onPointerLeave={onPlayerPointerLeave}
    >
      <div
        ref={stageRef}
        className={cn('reel-stage', isLightPage && 'is-light-page', onSwipeVertical && 'is-swipe-y')}
        tabIndex={0}
        role="region"
        aria-roledescription="story"
        aria-label={labels.region(reel.studyTitle)}
        onPointerDown={onStagePointerDown}
        onPointerUp={onStagePointerUp}
        onPointerCancel={() => {
          pointer.current.isDown = false
        }}
        data-testid="reel-stage"
      >
        <div className="reel-pages">
          {pages.map((page, index) => (
            <article
              key={page.key}
              ref={(node) => {
                pageRefs.current[index] = node
              }}
              className={cn('reel-page', `reel-surface-${page.surface}`)}
              data-kind={page.kind}
              data-testid={`reel-page-${index}`}
            >
              {page.content}
              {page.sourceLabel && <div className="reel-src">{page.sourceLabel}</div>}
              <div className="reel-shade" />
              <div className="reel-dimmer" />
            </article>
          ))}
        </div>
        <div className="reel-top" />
        <div
          ref={barRef}
          className="reel-rbar"
          tabIndex={0}
          role="slider"
          aria-label={labels.slider}
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={shown + 1}
          aria-valuetext={labels.slidePosition(shown + 1, total, currentPage.title)}
          onPointerDown={onBarPointerDown}
          onPointerMove={onBarPointerMove}
          onPointerUp={onBarPointerUp}
          onKeyDown={onBarKeyDown}
          data-testid="reel-progress"
        >
          {pages.map((page, index) => (
            <i key={page.key}>
              <b
                ref={(node) => {
                  fillRefs.current[index] = node
                }}
                style={{ width: currentFillWidth(index, shown, isPlaying, partial) }}
              />
            </i>
          ))}
        </div>
        <div className="reel-head">
          {reel.goalLabel && <span className="reel-head-dot" />}
          {reel.goalLabel && <span>{reel.goalLabel}</span>}
          <span className="reel-head-n">{labels.respondents(reel.respondentCount)}</span>
        </div>
        {controls === 'play' && <div className="reel-play-float">{playButton}</div>}
        {stageAction && controls !== 'play' && (
          <div className="reel-stage-action" data-testid="reel-stage-action">
            {stageAction}
          </div>
        )}
        {flash.key > 0 && (
          <div key={flash.key} className="reel-flash" aria-hidden data-testid="reel-flash">
            {flash.isPlaying ? <Play /> : <Pause />}
          </div>
        )}
      </div>
      {controls === 'full' && (
      <div className="reel-ctl">
        <button type="button" aria-label={labels.previous} onClick={previous} data-testid="reel-previous">
          <ChevronLeft aria-hidden />
        </button>
        <button type="button" aria-label={labels.next} onClick={next} data-testid="reel-next">
          <ChevronRight aria-hidden />
        </button>
        {playButton}
        <span className="reel-name">{currentPage.title}</span>
        <span className="reel-count" data-testid="reel-count">{`${shown + 1} / ${total}`}</span>
      </div>
      )}
    </div>
  )
}
